import React, { useState } from "react";
import { func, string, object } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { Link } from "@quintype/components";
import { parseUrl } from "query-string";
import assetify from "@quintype/framework/assetify";

import TextField from "../../atoms/TextField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import WithSSO from "../../with-sso";
import Headline from "../../atoms/Headline";
import Logo from "../../../../assets/images/palo-bangla.svg";
import BackIcon from "../../../../assets/images/back-icon.svg";

import { forgotPassword } from "../../helper/api";
import { isValidEmail, getContinueToText } from "../../utils/utils";
import { setLoading } from "../../helper/actions";

import "./forgotPassword.m.css";

const ForgotPassword = ({ setLoading, authHost, navigateToPage, sketchesHost, publisherAttributes, currentPath }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  const setData = (e) => {
    setEmail(e.target.value);

    // If error has been set then remove it
    if (e.target.value && emailError) {
      setEmailError("");
    }

    if (e.target.value && error) {
      setError(null);
    }

    if (e.target.value && success) {
      setSuccess(null);
    }
  };

  const onSubmitClick = (event) => {
    event.preventDefault();

    // true -> field errors, false - no fields errors
    if (validate()) {
      return;
    }

    setLoading(true);

    forgotPassword(email, redirectUrl)
      .then(() =>
        setSuccess(
          `We have sent a reset link via email ${
            email ? `to ${email}` : ""
          }. Please follow the link to reset your password`
        )
      )
      .catch((ex) => setError(ex.message))
      .finally(() => setLoading(false));
  };

  const validate = () => {
    let flag = false;

    if (!email) {
      setEmailError("Email address is required");
      flag = true;
    } else if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      flag = true;
    }

    return flag;
  };

  return (
    <div styleName={`${publisherAttributes.lang}-container`}>
      <form onSubmit={onSubmitClick}>
        <div styleName="container">
          <div styleName="wrapper">
            <a styleName="back-button" href={redirectUrl}>
              <img src={`${assetify(BackIcon)}`} alt="Back Icon" />
              <span styleName="back-text">Back</span>
            </a>
            <div styleName="content">
              <img src={`${assetify(Logo)}`} alt="Prothom Alo" styleName="palo-logo" />
              <Headline text="Forgot Password" headerLevel={3} headlineType={3} className={"forgot-text"} />
              <div styleName="continue-to">
                <Headline text="to continue to" headerLevel={4} headlineType={4} className={"continue-to-text"} />
                <Headline text={targetText} headerLevel={4} headlineType={4} className={"target-site"} />
              </div>
              <div styleName="forgot-pwd-message">
                Enter your account email address and we’ll email you a link so that you can reset your password
              </div>

              <TextField
                value={email}
                error={emailError}
                placeholder="Enter your email address here"
                inputType="type2"
                name="email"
                onChange={setData}
              />

              <MessageView type={error ? "error" : "success"} text={error || success} />

              <Button content="CONTINUE" type="submit" buttonType="type3" />

              <span styleName="new-user">
                New to Prothom Alo?{" "}
                <WithSSO
                  ssoHost={authHost}
                  signUpPath="/sign-up-page"
                  redirectUrl={redirectUrl}
                  callbackUrl={callbackUrl}
                >
                  {({ signUpHref }) => (
                    <Link styleName="signup" href={signUpHref}>
                      Create an account
                    </Link>
                  )}
                </WithSSO>
              </span>
            </div>
          </div>

          <div styleName="terms-privacy">
            By proceeding, you agree to{" "}
            <a styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
              Terms of Use
            </a>{" "}
            and{" "}
            <a styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
              Privacy Policy
            </a>{" "}
            of Prothom Alo
          </div>
        </div>
      </form>
    </div>
  );
};

ForgotPassword.propTypes = {
  navigateToPage: func,
  setLoading: func,
  authHost: string,
  sketchesHost: string,
  publisherAttributes: object,
  currentPath: string,
};

const mapStateToProps = (state) => ({
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
});

const mapDispatchToProps = (dispatch) => ({
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ForgotPassword);
